import { useAuthorizedSheets } from "@/hooks/useAuthorizedCollections";
import { FontIcon } from "@fluentui/react";
import { Tooltip } from "@fluentui/react-components";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useAppRouteParams } from "../../../AppRoutes";
import { postFileVersionDataCSV } from "../../../api/files";
import { getAPIErrorDetailOrMesssage } from "../../../api/helpers";
import { Sheet } from "../../../domain/Sheet";
import { DragAndDropUploadData } from "../../DragAndDropFields";
import styles from "./index.module.css";

type writeStatus = "waiting" | "in_progress" | "success" | "failed";
const statusMap: {
  [key in writeStatus]: { class: string; message: string; iconName: string };
} = {
  waiting: {
    class: styles.waiting,
    message: "未実行",
    iconName: "CirclePause",
  },
  in_progress: {
    class: styles.processing,
    message: "実行中",
    iconName: "Sync",
  },
  success: {
    class: styles.success,
    message: "成功",
    iconName: "Completed",
  },
  failed: {
    class: styles.failed,
    message: "失敗",
    iconName: "ErrorBadge",
  },
};
type SheetCSVConnect = {
  fileId: string;
  sheetId: string;
  writeStatus: writeStatus;
};

export const useUploadSheetDataCSV = (
  uploadedFileList: DragAndDropUploadData[],
  isUploaded?: boolean,
  setIsUploaded?: (isUploaded: boolean) => void
) => {
  const { organizationId, versionId } = useAppRouteParams();

  const [csvSheetConnectList, setCSVSheetConnectList] = useState<
    SheetCSVConnect[]
  >([]);

  const [sheets] = useAuthorizedSheets(versionId);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ファイル名（シート名_YYYYMMDDHHMM.csv）からシート名を抽出する関数
  const extractSheetNameFromFileName = useCallback(
    (fileName: string): string | null => {
      const match = fileName.match(/^(.+)_\d{8}\d{2}\d{2}\.csv$/);
      return match ? match[1] : null;
    },
    []
  );

  const sortedSheets = useMemo(
    () =>
      [...sheets].sort((a, b) => {
        return Math.sign(a.sort - b.sort);
      }),
    [sheets]
  );

  // あるシートが、紐付け可能かを返す
  const isConnectableSheet = useCallback(
    (sheet: Sheet) => {
      //　参照シートを除外
      if (sheet.referTo) {
        return false;
      }
      // 紐付けられているファイルが存在しないものは紐付け可能
      return (
        csvSheetConnectList.find(({ sheetId }) => sheetId === sheet.id) ===
        undefined
      );
    },
    [csvSheetConnectList]
  );

  const getDefaultSheet = useCallback(
    (fileName: string) => {
      const sheetName = extractSheetNameFromFileName(fileName);
      if (!sheetName) return null;
      // 紐付け可能なシートかを確認
      const sheet = sortedSheets.find(
        ({ displayName }) => displayName === sheetName
      );
      if (!sheet) return null;
      if (!isConnectableSheet(sheet)) return null;
      return sheet;
    },
    [sortedSheets, isConnectableSheet]
  );

  useEffect(() => {
    const uploadedFileIDList = uploadedFileList.map(({ id }) => id);

    const initialConnects: SheetCSVConnect[] = uploadedFileList
      .map(({ id: fileId, file }) => {
        const sheet = getDefaultSheet(file.name);
        if (!sheet) return null;
        return {
          fileId,
          sheetId: sheet.id,
          writeStatus: "waiting" as writeStatus,
        };
      })
      .filter((x): x is SheetCSVConnect => x !== null);

    setCSVSheetConnectList((prev) => {
      // 1) まだ残っているファイルの項目だけ残す
      const kept = prev.filter((item) =>
        uploadedFileIDList.includes(item.fileId)
      );

      // 2) 新規に追加すべき項目だけピックアップ
      const toAdd = initialConnects.filter(
        (init) => !kept.some((item) => item.fileId === init.fileId)
      );

      return [...kept, ...toAdd];
    });
  }, [uploadedFileList, sortedSheets]);

  const runUpload = useCallback(async () => {
    setCSVSheetConnectList(
      csvSheetConnectList.map((item) => ({
        ...item,
        writeStatus: "in_progress" as writeStatus,
      }))
    );
    for (let i = 0; i < csvSheetConnectList.length; i++) {
      const { fileId, sheetId } = csvSheetConnectList[i];
      const { file } = uploadedFileList.find(({ id }) => id === fileId) ?? {};
      if (file) {
        try {
          await postFileVersionDataCSV({
            organizationId,
            versionId,
            file,
            sheetId,
          });
          setCSVSheetConnectList((prev) =>
            prev.map((item) =>
              item.fileId === fileId
                ? { ...item, writeStatus: "success" as writeStatus }
                : item
            )
          );
        } catch (error) {
          setCSVSheetConnectList((prev) =>
            prev.map((item) =>
              item.fileId === fileId
                ? { ...item, writeStatus: "failed" as writeStatus }
                : item
            )
          );
          const { errorMessage } = getAPIErrorDetailOrMesssage(error);
          setErrorMessage(errorMessage);
          throw error;
        }
      }

      // APIの実行時の制限に従い、0.5秒待機
      // ref: [レートリミット: どの1秒間(ウィンドウ)についても「ウィンドウのコスト」が6を超えないこと。](https://www.notion.so/algo-artis/SSUI-API-2025-05-13ver-184af951024b807897a6cf64f1f2fb91?pvs=4#184af951024b806692d8f529eebbc756)
      // そのため、APIの実行時に0.5秒待機することで、レートリミットを回避する。
      if (i < csvSheetConnectList.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    setIsUploaded?.(true);
  }, [uploadedFileList, csvSheetConnectList]);

  const isDisablePrimaryButton =
    uploadedFileList.length === 0 ||
    csvSheetConnectList.length === 0 ||
    csvSheetConnectList.length !== uploadedFileList.length ||
    csvSheetConnectList.some(({ writeStatus }) => writeStatus !== "waiting") ||
    isUploaded === true;

  const isDisableCancelButton = csvSheetConnectList.some(
    ({ writeStatus }) => writeStatus === "in_progress"
  );

  const getExtElement = (id: string) => {
    const fileData = csvSheetConnectList.find((file) => file.fileId === id);
    const status = fileData?.writeStatus ?? "waiting";
    const {
      class: statusClass,
      message: statusMessage,
      iconName: statusIconName,
    } = statusMap[status];

    return (
      <div className={styles.info}>
        {status === "failed" ? (
          <div className={statusClass}>
            <div className={styles.status}>
              <FontIcon iconName={statusIconName} />
              <Tooltip content={errorMessage || ""} relationship="description">
                <div className={styles.hoverable}>{statusMessage}</div>
              </Tooltip>
            </div>
          </div>
        ) : (
          <div className={statusClass}>
            <div className={styles.status}>
              <FontIcon iconName={statusIconName} />
              <div>{`${statusMessage}`}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return {
    runUpload,
    isDisablePrimaryButton,
    isDisableCancelButton,
    csvSheetConnectList,
    setCSVSheetConnectList,
    getExtElement,
  };
};
